// src/pages/History/GameRoundHistory.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

// ── Helpers ───────────────────────────────────────────────────────────────────
const toIST = (utcValue, withSeconds = false) => {
  if (!utcValue) return '-';
  const d = new Date(utcValue);
  if (isNaN(d)) return '-';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
    hour12: true,
  });
};

const toISTTime = (utcValue) => {
  if (!utcValue) return '-';
  const d = new Date(utcValue);
  if (isNaN(d)) return '-';
  return d.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const getTodayDate = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', minimumFractionDigits: 2,
  }).format(amount);
};

const getSuitIcon = (suit) => {
  switch (suit?.toLowerCase()) {
    case 'clubs':    return '/img/Clubs.png';
    case 'diamonds': return '/img/Diamonds.png';
    case 'hearts':   return '/img/Hearts.png';
    case 'spades':   return '/img/Spades.png';
    default:         return '';
  }
};

const getStatusBadgeClass = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'badge bg-success';
    case 'active':    return 'badge bg-warning text-dark';
    case 'upcoming':  return 'badge bg-info text-dark';
    default:          return 'badge bg-secondary';
  }
};

const GAME_LABELS = {
  classic:   'RoyalKing',
  roulette:  'Roulette',
  funtarget: 'Fun Target',
};

// ── Result cell — differs per game ────────────────────────────────────────────
const ResultCell = ({ round }) => {
  if (round.gameType === 'classic') {
    const card = round.result?.card || round.card || '-';
    const suit = round.result?.suit || round.suit;
    return (
      <span className="d-flex align-items-center justify-content-center gap-1">
        <strong>{card}</strong>
        {suit && <img src={getSuitIcon(suit)} alt={suit} style={{ width: 22, height: 22 }} />}
      </span>
    );
  }
  // roulette: result.number  |  funtarget: result.target
  const num = round.result?.number ?? round.result?.target ?? round.number ?? '-';
  return <strong>{num}</strong>;
};

// ── Component ─────────────────────────────────────────────────────────────────
const GameRoundHistory = () => {
  const [loading, setLoading]         = useState(false);
  const [rounds, setRounds]           = useState([]);
  const [drawList, setDrawList]       = useState([]);  // flat list for the dropdown
  const [selectedRound, setSelectedRound] = useState(null);
  const [showModal, setShowModal]     = useState(false);

  const [filters, setFilters] = useState({
    gameType: 'classic',
    gameId:   '',           // specific draw filter (optional)
    status:   '',
    date:     getTodayDate(),
  });

  const [pagination, setPagination] = useState({
    page: 1, limit: 20, total: 0, pages: 0,
  });

  // ── fetch draw list for dropdown ──────────────────────────────────────────
  const fetchDrawList = useCallback(async () => {
    try {
      const res = await api.get('/admin/games/list', {
        params: {
          gameType: filters.gameType,
          date:     filters.date || undefined,
        },
      });
      // API returns { games: { classic: [], roulette: [], funtarget: [] } }
      const gamesObj = res.data?.games || {};
      const list = gamesObj[filters.gameType] || [];
      setDrawList(list);
    } catch (err) {
      console.error('Error fetching draw list:', err);
    }
  }, [filters.gameType, filters.date]);

  // ── fetch rounds ──────────────────────────────────────────────────────────
  const fetchRounds = useCallback(async (page = pagination.page) => {
    setLoading(true);
    try {
      const params = {
        gameType: filters.gameType,
        page,
        limit: pagination.limit,
      };
      if (filters.gameId) params.gameId = filters.gameId;
      if (filters.status) params.status = filters.status;
      if (filters.date)   params.date   = filters.date;

      const res = await api.get('/admin/games/rounds', { params });
      setRounds(res.data.rounds || []);
      setPagination(prev => ({
        ...prev,
        page,
        total: res.data.pagination?.total ?? 0,
        pages: res.data.pagination?.pages ?? 1,
      }));
    } catch (err) {
      toast.error('Failed to fetch game rounds');
      console.error('Error fetching rounds:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Refresh both when filters change
  useEffect(() => {
    setFilters(prev => ({ ...prev, gameId: '' })); // reset draw filter on game/date change
  }, [filters.gameType, filters.date]);

  useEffect(() => { fetchDrawList(); }, [fetchDrawList]);
  useEffect(() => { fetchRounds(1); }, [filters.gameType, filters.gameId, filters.status, filters.date]);
  useEffect(() => { fetchRounds(pagination.page); }, [pagination.page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // ── modal helpers ─────────────────────────────────────────────────────────
  const openModal  = (round) => { setSelectedRound(round); setShowModal(true); };
  const closeModal = ()      => { setShowModal(false); setSelectedRound(null); };

  // ── financial distribution ────────────────────────────────────────────────
  const calculateDistribution = (round) => {
    const totalBet       = round.totalBetAmount       || 0;
    const totalWinning   = round.totalWinningAmount   || 0;
    const distributed    = round.distributedAmount    || 0;
    const previousCarry  = round.previousCarryForward || 0;
    const carryForward   = round.carryForwardAmount   || 0;
    const availableForDist = round.availableForDistribution || 0;

    let winningPercentage    = 0;
    let commissionPercentage = 0;
    let commissionAmount     = 0;

    if (totalBet > 0 && availableForDist > 0) {
      const currentContrib  = availableForDist - previousCarry;
      commissionAmount      = totalBet - currentContrib;
      commissionPercentage  = Math.round((commissionAmount / totalBet) * 10000) / 100;
      winningPercentage     = Math.round((100 - commissionPercentage) * 100) / 100;
    } else if (totalBet > 0 && distributed > 0) {
      const net = distributed - previousCarry;
      if (net > 0) {
        commissionAmount     = totalBet - net;
        commissionPercentage = Math.round((commissionAmount / totalBet) * 10000) / 100;
        winningPercentage    = 100 - commissionPercentage;
      }
    }

    return {
      totalBet, totalWinning, distributed,
      previousCarry, carryForward, availableForDist,
      winningPercentage:    Math.max(0, winningPercentage),
      commissionPercentage: Math.max(0, commissionPercentage),
      commissionAmount:     Math.max(0, commissionAmount),
    };
  };

  // ── table columns differ slightly per game ────────────────────────────────
  const isClassic = filters.gameType === 'classic';

  const startEntry = (pagination.page - 1) * pagination.limit + 1;
  const endEntry   = Math.min(pagination.page * pagination.limit, pagination.total);

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <div className="game-round-history-page">
      <div className="container-fluid">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">Game Round History</h1>
        </div>

        <div className="admin-card-one">
          <div className="admin-card-header">
            <h2 className="admin-card-heading">Game Round Details</h2>
          </div>
          <div className="admin-card-body">

            {/* ── Filters ── */}
            <div className="row g-3 mb-4 align-items-end">
              {/* Game Type */}
              <div className="col-md-2">
                <label className="admin-label-one">Game</label>
                <select
                  name="gameType"
                  value={filters.gameType}
                  onChange={handleFilterChange}
                  className="form-select admin-select-one"
                >
                  <option value="classic">RoyalKing</option>
                  <option value="roulette">Roulette</option>
                  <option value="funtarget">Fun Target</option>
                </select>
              </div>

              {/* Draw (optional) */}
              <div className="col-md-3">
                <label className="admin-label-one">Draw (optional)</label>
                <select
                  name="gameId"
                  value={filters.gameId}
                  onChange={handleFilterChange}
                  className="form-select admin-select-one"
                >
                  <option value="">All Draws</option>
                  {drawList.map(d => (
                    <option key={d._id} value={d._id}>
                      {d.name} — {toISTTime(d.startTime)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div className="col-md-2">
                <label className="admin-label-one">Status</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="form-select admin-select-one"
                >
                  <option value="">All</option>
                  <option value="completed">Completed</option>
                  <option value="active">Active</option>
                  <option value="upcoming">Upcoming</option>
                </select>
              </div>

              {/* Date */}
              <div className="col-md-3">
                <label className="admin-label-one">Date (IST)</label>
                <input
                  type="date"
                  name="date"
                  value={filters.date}
                  onChange={handleFilterChange}
                  className="form-control admin-input-one"
                />
              </div>

              <div className="col-md-2">
                <button
                  className="admin-submit-one w-100"
                  onClick={() => fetchRounds(1)}
                  disabled={loading}
                >
                  {loading ? 'Loading…' : 'Generate Report'}
                </button>
              </div>
            </div>

            {/* ── Table ── */}
            <div className="table-responsive mt-2">
              <table className="table table-admin-one text-center">
                <thead className="table-purple">
                  <tr>
                    <th>Round ID</th>
                    <th>Time (IST)</th>
                    <th>Result</th>
                    <th>Total Bets</th>
                    <th>Bet Amount</th>
                    <th>Distributed</th>
                    <th>Carry Fwd</th>
                    <th>Manual</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading…</span>
                        </div>
                      </td>
                    </tr>
                  ) : rounds.length > 0 ? (
                    rounds.map((round, idx) => (
                      <tr key={round._id || idx}>
                        <td>{round.roundId || `#${round.drawNumber}`}</td>
                        <td>{toISTTime(round.time)}</td>
                        <td><ResultCell round={round} /></td>
                        <td>{round.totalBets || 0}</td>
                        <td>{formatCurrency(round.totalBetAmount)}</td>
                        <td className="text-success">{formatCurrency(round.distributedAmount)}</td>
                        <td className="text-warning">{formatCurrency(round.carryForwardAmount)}</td>
                        <td>
                          {round.isManualResult
                            ? <span className="badge bg-warning text-dark">Manual</span>
                            : <span className="badge bg-secondary">Auto</span>}
                        </td>
                        <td>
                          <span
                            className={getStatusBadgeClass(round.statusCode || round.status)}
                            style={{ cursor: round.statusCode === 'completed' ? 'pointer' : 'default' }}
                            onClick={() => round.statusCode === 'completed' && openModal(round)}
                            title={round.statusCode === 'completed' ? 'Click for details' : ''}
                          >
                            {round.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center py-4">No rounds found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
            {pagination.pages > 1 && (
              <div className="admin-table-bottom">
                <div className="table-info">
                  Showing {startEntry} to {endEntry} of {pagination.total} entries
                </div>
                <div className="table-pagination">
                  <button
                    className="page-btn prev"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                  >Prev</button>
                  {[...Array(Math.min(pagination.pages, 5))].map((_, i) => {
                    let p;
                    if (pagination.pages <= 5)                      p = i + 1;
                    else if (pagination.page <= 3)                  p = i + 1;
                    else if (pagination.page >= pagination.pages - 2) p = pagination.pages - 4 + i;
                    else                                            p = pagination.page - 2 + i;
                    return (
                      <button
                        key={p}
                        className={`page-btn ${pagination.page === p ? 'active' : ''}`}
                        onClick={() => setPagination(prev => ({ ...prev, page: p }))}
                      >{p}</button>
                    );
                  })}
                  <button
                    className="page-btn next"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                  >Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Round Detail Modal ── */}
      {showModal && selectedRound && (() => {
        const dist = calculateDistribution(selectedRound);
        const gt   = selectedRound.gameType;
        return (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Round Details — {selectedRound.roundId} &nbsp;
                    <span className="badge bg-secondary">{GAME_LABELS[gt] || gt}</span>
                    {selectedRound.isManualResult && (
                      <span className="badge bg-warning text-dark ms-1">Manual Result</span>
                    )}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
                <div className="modal-body">

                  {/* ── Round info + time ── */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <div className="card bg-light">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-2 text-muted">Round Information</h6>
                          <table className="table table-sm table-borderless mb-0">
                            <tbody>
                              <tr><td><strong>Round ID:</strong></td><td>{selectedRound.roundId}</td></tr>
                              <tr><td><strong>Draw Number:</strong></td><td>{selectedRound.drawNumber}</td></tr>
                              <tr>
                                <td><strong>Status:</strong></td>
                                <td><span className={getStatusBadgeClass(selectedRound.statusCode)}>{selectedRound.status}</span></td>
                              </tr>
                              <tr><td><strong>Declared At:</strong></td><td>{toIST(selectedRound.result?.declaredAt, true)}</td></tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card bg-light">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-2 text-muted">Time Information (IST)</h6>
                          <table className="table table-sm table-borderless mb-0">
                            <tbody>
                              <tr><td><strong>Start Time:</strong></td><td>{toIST(selectedRound.startTime)}</td></tr>
                              <tr><td><strong>End Time:</strong></td><td>{toIST(selectedRound.endTime)}</td></tr>
                              <tr><td><strong>Total Bets:</strong></td><td>{selectedRound.totalBets || 0}</td></tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Result ── */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card bg-success bg-opacity-10">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-3 text-muted">Result</h6>
                          {gt === 'classic' ? (
                            <div className="row text-center">
                              <div className="col-md-4">
                                <div className="border rounded p-3 bg-white">
                                  <small className="text-muted d-block">Card</small>
                                  <h3 className="mb-0">{selectedRound.result?.card || selectedRound.card || '-'}</h3>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="border rounded p-3 bg-white">
                                  <small className="text-muted d-block">Suit</small>
                                  <div className="d-flex justify-content-center align-items-center gap-2">
                                    {(selectedRound.result?.suit || selectedRound.suit) ? (
                                      <>
                                        <img src={getSuitIcon(selectedRound.result?.suit || selectedRound.suit)} style={{ width: 32, height: 32 }} alt="" />
                                        <span className="h5 mb-0 text-capitalize">{selectedRound.result?.suit || selectedRound.suit}</span>
                                      </>
                                    ) : '-'}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="border rounded p-3 bg-white">
                                  <small className="text-muted d-block">Full Card</small>
                                  <h5 className="mb-0">{selectedRound.result?.fullCard || '-'}</h5>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center">
                              <div className="border rounded p-3 bg-white d-inline-block px-5">
                                <small className="text-muted d-block">Result Number</small>
                                <h2 className="mb-0 text-primary">
                                  {gt === 'roulette'
                                    ? (selectedRound.result?.number ?? '-')
                                    : (selectedRound.result?.target ?? '-')}
                                </h2>
                                {gt === 'roulette' && selectedRound.result?.color && (
                                  <span className={`badge mt-1 bg-${selectedRound.result.color === 'red' ? 'danger' : selectedRound.result.color === 'black' ? 'dark' : 'success'}`}>
                                    {selectedRound.result.color}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Financial ── */}
                  <div className="row">
                    <div className="col-12">
                      <div className="card bg-light">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-3 text-muted">Financial Summary</h6>
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <table className="table table-sm mb-0">
                                <tbody>
                                  <tr>
                                    <td><strong>Total Bet Points:</strong></td>
                                    <td className="text-end">{formatCurrency(dist.totalBet)}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Commission ({dist.commissionPercentage.toFixed(1)}%):</strong></td>
                                    <td className="text-end text-danger">{formatCurrency(dist.commissionAmount)}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Distributed ({dist.winningPercentage.toFixed(1)}%):</strong></td>
                                    <td className="text-end text-success">{formatCurrency(dist.distributed)}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Total Winnings:</strong></td>
                                    <td className="text-end text-primary">{formatCurrency(dist.totalWinning)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <div className="col-md-6">
                              <table className="table table-sm mb-0">
                                <tbody>
                                  <tr>
                                    <td><strong>Previous Carry Forward:</strong></td>
                                    <td className="text-end">{formatCurrency(dist.previousCarry)}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Available for Distribution:</strong></td>
                                    <td className="text-end text-info">{formatCurrency(dist.availableForDist)}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Carry Forward:</strong></td>
                                    <td className="text-end text-warning">{formatCurrency(dist.carryForward)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Progress bar */}
                          {dist.totalBet > 0 && (
                            <>
                              <div className="progress" style={{ height: 28 }}>
                                <div
                                  className="progress-bar bg-danger"
                                  style={{ width: `${dist.commissionPercentage}%` }}
                                >
                                  {dist.commissionPercentage.toFixed(1)}% Commission
                                </div>
                                <div
                                  className="progress-bar bg-success"
                                  style={{ width: `${dist.winningPercentage}%` }}
                                >
                                  {dist.winningPercentage.toFixed(1)}% Winners
                                </div>
                              </div>
                              <div className="d-flex justify-content-between mt-1">
                                <small className="text-danger">{formatCurrency(dist.commissionAmount)}</small>
                                <small className="text-success">{formatCurrency(dist.distributed)}</small>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>Close</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default GameRoundHistory;